import React, { useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': { backgroundColor: theme.palette.error.dark },
  },
  warningText: { color: theme.palette.error.main, fontWeight: 600 },
}));

/**
 * Management card for AzureEnvironment entities.
 * Environments have no infrastructure of their own — just remove from catalog.
 * Blocked if any app's spec.dependsOn still references this environment
 * (detected client-side via the Relations graph).
 */
export function AzureDeleteEnvironmentCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRef = `azureenvironment:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();

  const envName = entity.metadata.name;
  const meta = entity.metadata as Record<string, any>;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const repoName = meta.adoRepoName as string | undefined;

  // Check if any component depends on this environment (from the entity's relations)
  const dependentComponents = (entity.relations ?? []).filter(
    r => r.type === 'dependencyOf' && r.targetRef.startsWith('component:'),
  );
  const hasDependents = dependentComponents.length > 0;

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<'idle' | 'deleting' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleDelete = async () => {
    setStatus('deleting');
    setErrorMsg('');
    try {
      // Remove catalog entity + location
      const locationAnnotation =
        entity.metadata.annotations?.['backstage.io/managed-by-location'];
      if (locationAnnotation) {
        try {
          const location = await catalogApi.getLocationByRef(locationAnnotation);
          if (location) await catalogApi.removeLocationById(location.id);
        } catch {
          // best-effort
        }
      }
      await catalogApi.removeEntityByUid(entity.metadata.uid!);

      // Best-effort: delete the ADO repo that holds this environment's catalog-info.yaml
      if (organization && project && repoName) {
        try {
          const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
          const { token = '' } = await identityApi.getCredentials();
          await fetch(
            `${baseUrl}/ado/repo?entityRef=${encodeURIComponent(entityRef)}&organization=${encodeURIComponent(organization)}&project=${encodeURIComponent(project)}&repoName=${encodeURIComponent(repoName)}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // non-fatal — user can delete the repo manually in ADO
        }
      }

      setStatus('done');
      setTimeout(() => navigate('/catalog'), 2000);
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message ?? 'Unknown error');
    }
  };

  return (
    <>
      <InfoCard title="Management">
        <Typography variant="body2" gutterBottom>
          Remove this environment from the catalog. No infrastructure is associated with
          environment entities — only the catalog entry is deleted.
        </Typography>
        {hasDependents && (
          <Typography variant="body2" color="error" gutterBottom>
            Warning: {dependentComponents.length} application(s) still depend on this environment.
            Remove those applications first.
          </Typography>
        )}
        <Button
          variant="contained"
          className={classes.dangerButton}
          startIcon={<DeleteForeverIcon />}
          onClick={() => setOpen(true)}
          disabled={hasDependents || status === 'deleting' || status === 'done'}
        >
          Delete Environment
        </Button>
      </InfoCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Environment: {envName}</DialogTitle>
        <DialogContent>
          {status === 'idle' && (
            <>
              <DialogContentText>
                This will remove <strong>{envName}</strong> from the Backstage catalog.
                No Azure infrastructure will be affected.
              </DialogContentText>
              <DialogContentText className={classes.warningText}>
                Type <strong>{envName}</strong> to confirm.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={envName}
                variant="outlined"
              />
            </>
          )}
          {status === 'deleting' && (
            <DialogContentText>Removing catalog entry…</DialogContentText>
          )}
          {status === 'done' && (
            <DialogContentText>Catalog entry removed. Redirecting…</DialogContentText>
          )}
          {status === 'error' && (
            <DialogContentText color="error">Error: {errorMsg}</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); setConfirmText(''); }} disabled={status === 'deleting'}>
            Cancel
          </Button>
          <Button
            className={classes.dangerButton}
            variant="contained"
            onClick={handleDelete}
            disabled={confirmText !== envName || status === 'deleting' || status === 'done'}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
